import { PropsWithChildren, useEffect, lazy, Suspense } from 'react';
import AutoLogout from 'react-components/auth/auto-logout/auto-logout';
import {
  actingUserAppSelectors,
  verifyUser,
} from 'redux/slices/actingUserSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { authService } from 'redux/utils/auth/auth-service';
import Spinner from 'toolkit/progress/spinner';
import { useAppLocation } from './useAppLocation';
import { useAppNavigation } from './useAppNavigation';
import { Box } from '@mui/material';
import { AppHeader, IconMessage } from '@act/storybook';
import AppGrid from '../AppGrid';
import RouteLoadingFallback from './RouteLoadingFallback';

const AdminDashboardSideMenu = lazy(() =>
  import('react-components/admin-dashboard/AdminDashboardSideMenu').then(
    module => ({
      default: module.default,
    })
  )
);

const CarePortalSideMenu = lazy(() =>
  import('../CarePortal/CarePortalSideMenu').then(module => ({
    default: module.default,
  }))
);

const ErrorPage = lazy(() =>
  import('./routes/StatusPages/ErrorPage').then(module => ({
    default: module.default,
  }))
);

const ProtectedRoute = ({ children }: PropsWithChildren) => {
  const loadingStatus = actingUserAppSelectors.useSelectLoadingStatus();
  const authError = actingUserAppSelectors.useSelectActingUserError();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const dispatch = useAppDispatch();
  const appLocation = useAppLocation();
  const navigate = useAppNavigation();

  const handleLogout = () => authService.logout();

  useEffect(() => {
    if (loadingStatus === 'initial') {
      dispatch(verifyUser({}));
    }
    if (loadingStatus === 'failed') {
      setTimeout(handleLogout, 1500);
    }
  }, [loadingStatus, dispatch]);

  // Handle access control navigation
  useEffect(() => {
    if (loadingStatus === 'success' && !actingUser?.hasAppAccess) {
      navigate.toNoAccess();
    }
  }, [loadingStatus, actingUser?.hasAppAccess, navigate]);

  if (loadingStatus === 'success') {
    const sideMenuContent =
      appLocation === 'admin-dashboard' ? (
        <AdminDashboardSideMenu />
      ) : (
        <CarePortalSideMenu />
      );

    return (
      <>
        <AutoLogout />
        <AppGrid
          headerContent={<AppHeader />}
          sideMenuContent={sideMenuContent}
          mainContent={children}
          isOnlyContentPage={appLocation === 'avon-patient'}
        />
      </>
    );
  } else if (loadingStatus === 'failed') {
    if (authError.code === '401') {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <IconMessage
            message="Not logged in"
            subMessage="Redirecting you to login page"
            iconName="UserCircleDashed"
            iconColor="error"
          />
        </Box>
      );
    } else {
      return (
        <Suspense fallback={<RouteLoadingFallback />}>
          <ErrorPage />
        </Suspense>
      );
    }
  } else {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Spinner centered={true} />
      </Box>
    );
  }
};

export default ProtectedRoute;
